import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProductHeader from './components/ProductHeader';
// import StockAdjustment from './components/StockAdjustment';
import LocationStock from './components/LocationStock';
// import SupplierInfo from './components/SupplierInfo';
import TransactionHistory from './components/TransactionHistory';
// import BarcodeScanner from './components/BarcodeScanner';
import KeyMetrics from './components/KeyMetrics';
import api from '../../utils/api';
import { toast } from 'sonner';

const ProductDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('metrics');
  const [loading, setLoading] = useState(true);

  // Product data state
  const [product, setProduct] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [metrics, setMetrics] = useState([]);

  // Fetch product data and related transaction data when component mounts
  useEffect(() => {
    const fetchProductData = async () => {
      if (!id) {
        toast.error('Product ID is missing');
        navigate('/product-catalog');
        return;
      }

      try {
        setLoading(true);
        const { data } = await api.get(`/products/${id}`);
        
        // Transform API data to match component expectations
        const productData = {
          id: data.id,
          name: data.name,
          sku: data.sku || '',
          description: data.description || '',
          category: Array.isArray(data.category) ? data.category.join(', ') : (data.category || ''),
          image: data.imageUrl || 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=400&fit=crop',
          barcode: data.barcode || '',
          totalStock: data.quantity || 0,
          unitPrice: data.price || 0,
          cost: data.cost || 0,
          reorderPoint: data.reorderPoint || 0,
          supplier: data.supplier || '',
          weight: data.weight || null,
          dimensions: data.dimensions || null,
          locations: data.locations || [],
          isActive: data.isActive !== false,
          trackInventory: data.trackInventory !== false,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          stockStatus: data.quantity > 0 ? (data.quantity <= data.reorderPoint ? 'Low Stock' : 'In Stock') : 'Out of Stock'
        };
        
        setProduct(productData);
        
        // Fetch transaction history and analytics data based on barcode
        if (data.barcode) {
          await fetchTransactionData(data.barcode);
          await fetchAnalyticsData(data.barcode, data.quantity || 0);
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    const fetchTransactionData = async (barcode) => {
      try {
        // Fetch cart data (pending transactions)
        const cartResponse = await api.get('/cart');
        const cartItems = cartResponse.data.filter(item => 
          item.barcode === barcode || item.sku === barcode
        );

        // Fetch checkout data (completed transactions)
        const checkoutResponse = await api.get('/checkouts');
        const checkoutItems = checkoutResponse.data.filter(item => 
          item.barcode === barcode || item.sku === barcode
        );

        // Combine and format transaction history
        const allTransactions = [];

        // Add cart items as pending transactions
        cartItems.forEach(item => {
          allTransactions.push({
            id: `cart-${item.id}`,
            type: 'pending_sale',
            quantity: item.quantity,
            location: 'cart',
            date: new Date(item.createdAt?.toDate ? item.createdAt.toDate() : item.createdAt),
            user: 'Current User',
            reference: `CART-${item.id}`,
            notes: `Pending sale - ${item.name}`,
            balanceAfter: null, // Will be calculated
            price: item.price,
            cost: item.cost
          });
        });

        // Add checkout items as completed sales
        checkoutItems.forEach(item => {
          allTransactions.push({
            id: `checkout-${item.id}`,
            type: 'sale',
            quantity: item.quantity,
            location: 'checkout',
            date: new Date(item.createdAt?.toDate ? item.createdAt.toDate() : item.createdAt),
            user: item.userId || 'Unknown',
            reference: item.mainCheckoutId ? `CHK-${item.mainCheckoutId}` : `CHK-${item.id}`,
            notes: `Sale completed - ${item.name}`,
            balanceAfter: null, // Will be calculated
            price: item.price,
            cost: item.cost
          });
        });

        // Sort by date (newest first)
        allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));

        setTransactions(allTransactions);
      } catch (error) {
        console.error('Failed to fetch transaction data:', error);
        // Set empty array on error
        setTransactions([]);
      }
    };

    const fetchAnalyticsData = async (barcode, currentStock) => {
      try {
        // Fetch checkout data for analytics
        const checkoutResponse = await api.get('/checkouts');
        const productCheckouts = checkoutResponse.data.filter(item => 
          item.barcode === barcode || item.sku === barcode
        );

        // Calculate metrics
        const totalSold = productCheckouts.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const totalRevenue = productCheckouts.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0);
        
        // Calculate time-based metrics
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        const recentSales = productCheckouts.filter(item => {
          const itemDate = new Date(item.createdAt?.toDate ? item.createdAt.toDate() : item.createdAt);
          return itemDate >= thirtyDaysAgo;
        });
        
        const monthlySales = recentSales.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const dailyVelocity = monthlySales / 30;
        const daysOfSupply = dailyVelocity > 0 ? Math.round(currentStock / dailyVelocity) : 999;
        const annualTurnover = dailyVelocity > 0 ? (dailyVelocity * 365) / currentStock : 0;

        const calculatedMetrics = [
          {
            id: 1,
            type: 'movement',
            label: 'Monthly Sales',
            value: monthlySales.toString(),
            change: totalSold > monthlySales ? `+${((monthlySales / (totalSold - monthlySales)) * 100).toFixed(1)}%` : 'New',
            trend: monthlySales > 0 ? 'up' : 'stable'
          },
          {
            id: 2,
            type: 'supply',
            label: 'Days of Supply',
            value: daysOfSupply > 999 ? '∞' : daysOfSupply.toString(),
            change: daysOfSupply < 30 ? 'Low' : daysOfSupply > 90 ? 'High' : 'Normal',
            trend: daysOfSupply < 30 ? 'down' : daysOfSupply > 90 ? 'up' : 'stable'
          },
          {
            id: 3,
            type: 'turnover',
            label: 'Annual Turnover',
            value: `${annualTurnover.toFixed(1)}x`,
            change: annualTurnover > 2 ? 'Good' : annualTurnover > 1 ? 'Fair' : 'Low',
            trend: annualTurnover > 2 ? 'up' : annualTurnover > 1 ? 'stable' : 'down'
          },
          {
            id: 4,
            type: 'velocity',
            label: 'Daily Velocity',
            value: dailyVelocity.toFixed(1),
            change: dailyVelocity > 1 ? 'Active' : dailyVelocity > 0.1 ? 'Slow' : 'Stagnant',
            trend: dailyVelocity > 1 ? 'up' : dailyVelocity > 0.1 ? 'stable' : 'down'
          }
        ];

        setMetrics(calculatedMetrics);
      } catch (error) {
        console.error('Failed to fetch analytics data:', error);
        // Set default metrics on error
        setMetrics([
          { id: 1, type: 'movement', label: 'Monthly Sales', value: '0', change: 'No data', trend: 'stable' },
          { id: 2, type: 'supply', label: 'Days of Supply', value: '∞', change: 'No sales', trend: 'stable' },
          { id: 3, type: 'turnover', label: 'Annual Turnover', value: '0x', change: 'No data', trend: 'stable' },
          { id: 4, type: 'velocity', label: 'Daily Velocity', value: '0', change: 'No data', trend: 'stable' }
        ]);
      }
    };

    fetchProductData();
  }, [id, navigate]);

  // Location data - using product locations or default
  const [locations] = useState([
    {
      id: 'main-warehouse',
      name: 'Main Warehouse',
      address: '123 Storage Ave, City, State',
      stock: 0, // Will be updated with real data
      available: 0,
      reserved: 0,
      reorderPoint: 0,
      lastUpdated: 'Loading...'
    }
  ]);

  const tabs = [
    { id: 'history', label: 'Transaction History', icon: 'History' },
    { id: 'metrics', label: 'Analytics', icon: 'BarChart3' }
  ];

  const handleProductEdit = (updatedProduct) => {
    setProduct(updatedProduct);
  };

  // Removed duplicate handler
  // const handleProductDuplicate = () => {
  //   navigate('/product-catalog', { 
  //     state: { duplicateProduct: product } 
  //   });
  // };

  const handleStockAdjustment = (adjustment) => {
    console.log('Stock adjustment:', adjustment);
    // In a real app, this would update the backend and refresh data
  };

  const handleStockTransfer = (transfer) => {
    console.log('Stock transfer:', transfer);
    // In a real app, this would update the backend and refresh data
  };

  const handleCreatePO = (purchaseOrder) => {
    console.log('Purchase order created:', purchaseOrder);
    // In a real app, this would create the PO and navigate to PO management
  };

  const handleScanComplete = (scanResult) => {
    console.log('Barcode scan completed:', scanResult);
    // In a real app, this would trigger stock update or other actions
  };

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        <Breadcrumb />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
        <Breadcrumb />
        <div className="flex flex-col items-center justify-center h-64">
          <Icon name="AlertCircle" size={48} className="text-error mb-4" />
          <h2 className="text-xl font-medium text-foreground mb-2">Product Not Found</h2>
          <p className="text-muted-foreground mb-4">The product you're looking for doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/product-catalog')}>Back to Product Catalog</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
      <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/product-catalog')}
                className="shrink-0"
              >
                <Icon name="ArrowLeft" size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-semibold text-foreground">Product Details</h1>
                <p className="text-muted-foreground mt-1">
                  Comprehensive product management and inventory tracking
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                iconName="ExternalLink"
                iconPosition="left"
                onClick={() => navigate('/reports')}
              >
                View Reports
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/product-catalog')}
              >
                Add New Product
              </Button>
            </div>
          </div>

          {/* Product Header */}
          <div className="mb-8">
            <ProductHeader
              product={product}
              onEdit={handleProductEdit}
            />
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-border mb-8">
            <nav className="flex space-x-8 overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-8">
            {false && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Overview removed */}
              </div>
            )}

            {false && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Stock Adjustment removed */}
                <LocationStock
                  locations={locations}
                  onTransfer={handleStockTransfer}
                />
              </div>
            )}

            {/* Supplier section removed */}
            {/* {activeTab === 'suppliers' && ( */}
            {/*   <SupplierInfo */}
            {/*     suppliers={suppliers} */}
            {/*   /> */}
            {/* )} */}

            {activeTab === 'history' && (
              <TransactionHistory transactions={transactions} />
            )}

            {activeTab === 'metrics' && (
              <KeyMetrics 
                metrics={metrics} 
                product={product}
                movementData={null} // Will be calculated from real data
                turnoverData={null} // Will be calculated from real data
              />
            )}
          </div>
        </div>
  );
};

export default ProductDetails;